import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, SignInParams, SignUpParams } from '@/services/auth/cognito';

interface User {
  userId: string;
  username: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (params: SignInParams) => Promise<void>;
  signUp: (params: SignUpParams) => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      console.log('Checking authentication...');
      const currentUser = await authService.getCurrentUser();
      console.log('Current user:', currentUser);

      if (currentUser) {
        const userData = {
          userId: currentUser.userId,
          username: currentUser.username,
        };
        console.log('Setting user:', userData);
        setUser(userData);
      } else {
        console.log('No current user found');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function signIn(params: SignInParams) {
    try {
      const result = await authService.signIn(params);
      console.log('Sign in result:', result);

      // Always check auth after sign-in attempt
      await checkAuth();

      // Verify we have a user
      const currentUser = await authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('Authentication failed - no user found');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  async function signUp(params: SignUpParams) {
    await authService.signUp(params);
  }

  async function signOut() {
    await authService.signOut();
    setUser(null);
  }

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
